import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  getCommittees,
  createCommittee,
  updateCommittee,
  deleteCommittee,
} from "../api/api";
import { useAuth } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import LotusIcon from "../components/LotusIcon";

const CommitteesList = () => {
  const { currentUser, isAuthenticated } = useAuth();
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [selectedCommittee, setSelectedCommittee] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "primary",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [committeeToDelete, setCommitteeToDelete] = useState(null);

  // Available colors for committees
  const colorOptions = [
    { value: "primary", label: "Blue" },
    { value: "success", label: "Green" },
    { value: "danger", label: "Red" },
    { value: "warning", label: "Yellow" },
    { value: "info", label: "Teal" },
    { value: "secondary", label: "Gray" },
    { value: "dark", label: "Black" },
  ];

  useEffect(() => {
    fetchCommittees();
  }, [isAuthenticated]);

  const fetchCommittees = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getCommittees();
      setCommittees(response || []);
    } catch (err) {
      console.error("Error fetching committees:", err);
      setError("Failed to load committees. Please try again later.");
      setCommittees([]); // Initialize with empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, committee = null) => {
    setModalMode(mode);
    setSelectedCommittee(committee);

    if (mode === "edit" && committee) {
      setFormData({
        name: committee.name || "",
        description: committee.description || "",
        color: committee.color || "primary",
      });
    } else {
      // Reset form for create mode
      setFormData({
        name: "",
        description: "",
        color: "primary",
      });
    }

    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCommittee(null);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Committee name is required";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (modalMode === "create") {
        const newCommittee = await createCommittee(formData);
        setCommittees([...committees, newCommittee]);
      } else if (modalMode === "edit" && selectedCommittee) {
        const updatedCommittee = await updateCommittee(
          selectedCommittee._id,
          formData
        );
        setCommittees(
          committees.map((c) =>
            c._id === selectedCommittee._id ? updatedCommittee : c
          )
        );
      }

      handleCloseModal();
    } catch (err) {
      console.error("Error saving committee:", err);
      setFormErrors({
        submit:
          err.response?.data?.message ||
          "Failed to save committee. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (committee) => {
    setCommitteeToDelete(committee);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCommitteeToDelete(null);
  };

  const handleDeleteCommittee = async () => {
    if (!committeeToDelete) return;

    try {
      await deleteCommittee(committeeToDelete._id);
      setCommittees(committees.filter((c) => c._id !== committeeToDelete._id));
      handleCloseDeleteModal();
    } catch (err) {
      console.error("Error deleting committee:", err);
      setError("Failed to delete committee. Please try again later.");
      handleCloseDeleteModal();
    }
  };

  return (
    <Container>
      <div className="section-title d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <LotusIcon className="me-3" size={32} />
          <h1 className="mb-0">Committees</h1>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal("create")}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          New Committee
        </Button>
      </div>

      {/* Error message */}
      {error && <Alert variant="danger">{error}</Alert>}

      {/* Loading spinner */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : committees && committees.length > 0 ? (
        <Row>
          {committees.map((committee) => (
            <Col md={6} lg={4} key={committee._id} className="mb-4">
              <Card className="h-100 shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <Badge
                    bg={committee.color || "primary"}
                    className="px-3 py-2"
                  >
                    {committee.name}
                  </Badge>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleOpenModal("edit", committee)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleOpenDeleteModal(committee)}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Card.Text>
                    {committee.description || "No description available."}
                  </Card.Text>
                  <div className="mt-3">
                    <small className="text-muted">
                      <strong>Members:</strong> {committee.memberCount || 0}
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="text-center py-5">
          <Card.Body>
            <h3>No committees found</h3>
            <p className="text-muted">
              Get started by creating a new committee.
            </p>
            <Button variant="primary" onClick={() => handleOpenModal("create")}>
              Create New Committee
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Committee Modal (Create/Edit) */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" ? "Create New Committee" : "Edit Committee"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {formErrors.submit && (
              <Alert variant="danger">{formErrors.submit}</Alert>
            )}

            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Committee Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter committee name"
                isInvalid={!!formErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter committee description"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="color">
              <Form.Label>Color</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {colorOptions &&
                  colorOptions.map((color) => (
                    <div
                      key={color.value}
                      className={`
                      color-option p-2 rounded border
                      ${
                        formData.color === color.value
                          ? "border-dark"
                          : "border-light"
                      }
                    `}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setFormData({ ...formData, color: color.value })
                      }
                    >
                      <Badge
                        bg={color.value}
                        className="px-3 py-2 d-flex align-items-center"
                      >
                        {color.label}
                        {formData.color === color.value && (
                          <i className="bi bi-check-lg ms-2"></i>
                        )}
                      </Badge>
                    </div>
                  ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save Committee"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the committee "
            {committeeToDelete?.name}"?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCommittee}>
            Delete Committee
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CommitteesList;
